import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMerchantById,
  getMerchantStaff,
  getMerchantCreditAccounts,
  sendReminder,
} from '@/features/merchants/api';
import { MerchantStatusBadge } from '@/features/merchants/MerchantStatusBadge';
import { MerchantReviewPanel } from '@/features/merchants/MerchantReviewPanel';
import { useToast } from '@/hooks/use-toast';
import {
  RecordSaleDialog,
  RecordPaymentDialog,
  ChargesDialog,
  SettlementConfigDialog,
  AddStaffDialog,
  SETTLEMENT_CHANNELS,
  COLLECTION_MODES,
} from '@/features/merchants/MerchantCreditDialogs';
import type { CreditAccountItem } from '@/features/merchants/merchant';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  ShoppingBag,
  Users,
  DollarSign,
  CheckCircle,
  XCircle,
  User,
  UserPlus,
  Phone,
  Plus,
  Wallet,
  Settings,
} from '@/lib/icons';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { formatDate } from '@/lib/dates';

export const Route = createFileRoute('/_layout/merchants/$merchantId')({
  component: MerchantDetailPage,
});

/** Staff active/inactive pill. */
const statusBadge = (isActive: boolean) =>
  isActive ? (
    <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400">
      <CheckCircle className="w-3 h-3 mr-1" />
      Active
    </Badge>
  ) : (
    <Badge className="bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400">
      <XCircle className="w-3 h-3 mr-1" />
      Inactive
    </Badge>
  );

/** Staff role pill — owner is highlighted, staff stays neutral. */
const roleBadge = (role: string) => (
  <Badge
    variant="outline"
    className={
      role === 'owner'
        ? 'border-amber-500/30 text-amber-600 dark:text-amber-400'
        : ''
    }
  >
    {role}
  </Badge>
);

/**
 * platform-service's `MerchantStaffResponse` carries no phone/name — only the
 * internal `consumerSeqId`. Show that rather than an empty cell.
 */
const staffFallbackLabel = (consumerSeqId: number | null) =>
  consumerSeqId ? `Consumer #${consumerSeqId}` : '—';

function MerchantDetailPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { merchantId } = Route.useParams();

  const [saleOpen, setSaleOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payAccount, setPayAccount] = useState<CreditAccountItem | null>(null);
  const [chargesOpen, setChargesOpen] = useState(false);
  const [chargesAccount, setChargesAccount] = useState<CreditAccountItem | null>(
    null
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);

  const { toast } = useToast();

  const refreshAccounts = () =>
    queryClient.invalidateQueries({
      queryKey: ['merchant-credit-accounts', merchantId],
    });

  const refreshStaff = () =>
    queryClient.invalidateQueries({
      queryKey: ['merchant-staff', merchantId],
    });

  const remindMutation = useMutation({
    mutationFn: (accountId: string) => sendReminder(merchantId, accountId),
    onSuccess: () =>
      toast({ title: 'Reminder sent', description: 'The customer was notified.' }),
    onError: (err: any) =>
      toast({
        variant: 'destructive',
        title: 'Could not send reminder',
        description: err?.message ?? 'Please try again.',
      }),
  });

  const {
    data: merchantData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['merchant', merchantId],
    queryFn: () => getMerchantById(merchantId),
  });

  const { data: staffData } = useQuery({
    queryKey: ['merchant-staff', merchantId],
    queryFn: () => getMerchantStaff(merchantId),
    enabled: !!merchantId,
  });

  const { data: accountsData } = useQuery({
    queryKey: ['merchant-credit-accounts', merchantId],
    queryFn: () => getMerchantCreditAccounts(merchantId),
    enabled: !!merchantId,
  });

  const merchant = merchantData?.value;
  const staff = staffData?.value ?? [];
  const accounts = accountsData?.value ?? [];

  const totalReceivables = accounts.reduce(
    (s, a) => s + a.runningBalance,
    0
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !merchant) {
    return (
      <div className="p-6">
        <ApiErrorCard
          error={error}
          onRetry={() => window.location.reload()}
          title="Failed to load merchant"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/merchants' })}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">
            {merchant.businessName}
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Merchant detail — manage staff, credit accounts, and receivables
          </p>
        </div>
        <MerchantStatusBadge status={merchant.status} />
      </div>

      <MerchantReviewPanel merchant={merchant} />

      {/* Info Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-lg">
              <Phone className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-mono text-muted-foreground">
                {merchant.ownerPhone}
              </p>
              <p className="text-xs text-muted-foreground">Owner Phone</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-medium capitalize">
                {COLLECTION_MODES.find(
                  (m) => m.value === merchant.collectionMode
                )?.label ?? merchant.collectionMode}
              </p>
              <p className="text-xs text-muted-foreground">Collection</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500 to-violet-600 text-white shadow-lg">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-bold">{accounts.length}</p>
              <p className="text-xs text-muted-foreground">Credit Accounts</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                ${totalReceivables.toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground">
                Total Receivables
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Settlement & Collection (F10 §2.6, §6.2) */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="h-5 w-5 text-muted-foreground" />
              Settlement & Collection
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSettingsOpen(true)}
            >
              <Settings className="w-3.5 h-3.5 mr-1.5" /> Configure
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid gap-6 sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Collection mode
            </p>
            <p className="mt-1 text-sm font-medium">
              {COLLECTION_MODES.find(
                (m) => m.value === merchant.collectionMode
              )?.label ?? merchant.collectionMode}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Settlement numbers
            </p>
            {Object.keys(merchant.settlementAccounts ?? {}).length === 0 ? (
              <p className="mt-1 text-sm text-muted-foreground">
                None configured yet.
              </p>
            ) : (
              <ul className="mt-1 space-y-0.5">
                {SETTLEMENT_CHANNELS.filter(
                  (ch) => merchant.settlementAccounts?.[ch.key]
                ).map((ch) => (
                  <li key={ch.key} className="text-sm">
                    <span className="text-muted-foreground">{ch.label}: </span>
                    <span className="font-mono">
                      {merchant.settlementAccounts[ch.key]}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Staff — the MerchantStaff link is what mints the merchant claims on a
          consumer's JWT at sign-in, i.e. what unlocks merchant mode in the app. */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              Staff Members ({staff.length})
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => setStaffOpen(true)}>
              <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add Staff
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Staff get merchant mode (&ldquo;My Shop&rdquo;) in the mobile app. They must
            already have an app account, and must sign out and sign in again after
            being added.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {staff.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <User className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm font-medium">No staff members yet</p>
              <p className="text-sm">
                Use <span className="font-medium">Add Staff</span> to give an existing
                app user access to this shop.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead>Staff member</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Added</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">
                      {s.consumerName || (
                        <span className="font-mono text-sm text-muted-foreground">
                          {s.consumerPhone || staffFallbackLabel(s.consumerSeqId)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{roleBadge(s.role)}</TableCell>
                    <TableCell>{statusBadge(s.isActive)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(s.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Credit Accounts */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-muted-foreground" />
              Credit Accounts ({accounts.length})
            </CardTitle>
            <Button
              size="sm"
              className="bg-gradient-to-r from-primary to-primary/85 hover:from-primary/90 hover:to-primary/75 text-primary-foreground shadow-md"
              onClick={() => setSaleOpen(true)}
            >
              <Plus className="w-4 h-4 mr-1.5" /> Record Sale
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {accounts.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm font-medium">No credit accounts yet</p>
              <p className="text-sm">
                Use <span className="font-medium">Record Sale</span> to post a credit
                sale — it opens the customer's account automatically.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead>Customer</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Credit Limit</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Opened</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-mono text-sm">
                      {a.customerMsisdn}
                    </TableCell>
                    <TableCell>{a.currency}</TableCell>
                    <TableCell className="text-right font-semibold">
                      ${a.runningBalance.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      ${a.creditLimit.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {a.overdue ? (
                        <Badge className="bg-red-500/15 text-red-600 border-red-500/30">
                          Overdue
                          {a.daysOutstanding ? ` · ${a.daysOutstanding}d` : ''}
                        </Badge>
                      ) : (
                        <Badge
                          className={
                            a.status === 'active'
                              ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30'
                              : 'bg-red-500/15 text-red-600 border-red-500/30'
                          }
                        >
                          {a.status}
                          {a.daysOutstanding ? ` · ${a.daysOutstanding}d` : ''}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(a.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setChargesAccount(a);
                            setChargesOpen(true);
                          }}
                        >
                          View
                        </Button>
                        {a.runningBalance > 0 ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={remindMutation.isPending}
                            onClick={() => remindMutation.mutate(a.id)}
                          >
                            Remind
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={a.runningBalance <= 0}
                          onClick={() => {
                            setPayAccount(a);
                            setPayOpen(true);
                          }}
                        >
                          <Wallet className="w-3.5 h-3.5 mr-1.5" /> Record Payment
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <RecordSaleDialog
        merchantId={merchantId}
        open={saleOpen}
        onOpenChange={setSaleOpen}
        onSuccess={refreshAccounts}
      />
      <RecordPaymentDialog
        merchantId={merchantId}
        account={payAccount}
        open={payOpen}
        onOpenChange={setPayOpen}
        onSuccess={refreshAccounts}
      />
      <ChargesDialog
        account={chargesAccount}
        open={chargesOpen}
        onOpenChange={setChargesOpen}
      />
      <SettlementConfigDialog
        merchant={merchant}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onSuccess={() =>
          queryClient.invalidateQueries({ queryKey: ['merchant', merchantId] })
        }
      />
      <AddStaffDialog
        merchantId={merchantId}
        merchantName={merchant.businessName}
        open={staffOpen}
        onOpenChange={setStaffOpen}
        onSuccess={refreshStaff}
      />
    </div>
  );
}
